import { KnowledgeArticle, KnowledgeCategory, KnowledgeChannel } from './types';
import { KnowledgeLocale } from './knowledgeCopy';

export function normKnowledgeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Client help articles to exclude (Urban Rewards retired, hero-only safety article). */
const HELP_REMOVED = new Set(
  [
    'Urban-Rewards-Rewards',
    'Urban-Rewards-Membership-levels',
    'Urban-Rewards-Collecting-points',
    'Keeping-clients-safe-Understanding-your-massage-table-s-weight-limit',
  ].map(normKnowledgeKey)
);

/** Pro help articles to retire or move off Hero. */
const HERO_REMOVED = new Set(
  [
    'What-is-Request-to-Book',
    'How-can-I-share-my-profile-on-my-social-media',
    'How-do-I-access-the-community-forum',
    'What-is-the-Urban-community-forum',
    'How-can-clients-find-the-code-of-conduct',
    'How-do-I-set-or-amend-my-availability',
    'Offering-CBD-massage-treatments',
    'What-should-I-know-as-a-Get-A-Drip-nurse-or-franchise-owner',
    'How-do-Get-a-Drip-consultations-and-treatments-work',
    'How-do-I-remove-a-treatment-from-my-profile',
    'Sharing-my-profile-with-friends',
    'Urban-Community-Youtube-Channel',
  ].map(normKnowledgeKey)
);

/** Misfiled client FAQs currently stored under Hero – served on /help instead. */
const HERO_TO_HELP = new Set(
  [
    'The-Urban-Code-of-Conduct',
    'Will-my-facial-include-steam',
    'I-m-allergic-to-latex-will-that-be-an-issue',
    'How-do-I-close-my-account',
    'How-to-contact-customer-support',
    'Can-I-get-a-receipt-for-my-booking',
    'Can-I-pay-with-cash',
    'How-Urban-defines-inappropriate-behaviour',
    'What-is-an-oncology-massage',
    'I-m-having-cancer-treatment-which-Urban-treatments-are-suitable',
    'Can-I-book-a-treatment-after-surgery',
    'I-have-a-skin-condition-or-an-allergy-can-I-request-specific-products',
    'Can-I-book-a-treatment-in-a-hospital-or-care-home',
    'I-have-a-disability-or-limited-mobility-how-will-my-pro-handle-this',
    'Why-do-I-need-to-submit-a-photo-with-my-beauty-complaint',
    'What-is-an-express-massage',
    'What-if-I-have-an-allergic-reaction',
    'How-we-keep-you-safe',
    'How-do-you-handle-complaints',
    'How-to-make-a-complaint',
    'How-does-your-refer-a-friend-scheme-work',
  ].map(normKnowledgeKey)
);

interface ArticlePatch {
  title?: string;
  bodyHtml?: string;
}

const ARTICLE_PATCHES: Record<string, ArticlePatch> = {
  [normKnowledgeKey('Massage')]: {
    title: 'Massage - Good to know',
  },
  [normKnowledgeKey('How-can-I-cancel-a-booking-directly-in-my-Hero-app')]: {
    title: 'How do I cancel a booking in the Urban Pro app?',
    bodyHtml: `<p>If the booking starts in more than 2 hours</p>
<ul>
<li>Open the booking and tap Cancel booking</li>
<li>Add a reason and any notes</li>
<li>You'll see a reminder of our cancellation policy before you confirm</li>
</ul>
<p>If the booking starts in less than 2 hours</p>
<ul>
<li>Open the booking and request a cancellation</li>
<li>Our customer support team will review your request – the booking isn't cancelled until they confirm</li>
<li>If approved, we'll update the booking status and let your client know it's been cancelled</li>
</ul>
<h3>Good to know</h3>
<ul>
<li>All cancellations are checked against the Pro cancellation policy and charges may apply.</li>
<li>Keep your diary and service areas up to date – when you add hours, clients see you as available</li>
<li>The more notice you give, the more time your client has to rebook. Where possible, cancel as early as you can to help them out</li>
</ul>`,
  },
  [normKnowledgeKey('Are-parking-or-transport-costs-covered')]: {
    bodyHtml:
      '<p>No – your rate already includes any travel or parking costs. We don\'t offer extra payments beyond what we agreed in your onboarding and partnership terms.</p>',
  },
  [normKnowledgeKey('How-do-I-change-my-bank-account-details')]: {
    bodyHtml: `<p>You can change your bank details in the Pro app.</p>
<ul>
<li>Go to 'View statements' from the dashboard in the app</li>
<li>Change your bank details and click save</li>
</ul>`,
  },
  [normKnowledgeKey('Are-there-business-cards-we-can-give-out-to-clients')]: {
    bodyHtml:
      '<p>We don\'t have business cards to give out, but you can encourage clients to add you as a favourite pro within their app.</p>',
  },
  [normKnowledgeKey('How-does-your-diary-work')]: {
    bodyHtml: `<p>Your diary is 100% managed by you. It's up to you to decide the days and times you would like to work.</p>
<p>You can add your availability either days or months in advance as well as adding it on the same day.</p>
<p>You're free to remove/manage your availability as much as you like, however if you have received a booking, we do expect you to honour the booking.</p>`,
  },
  [normKnowledgeKey('Where-can-I-find-my-contract')]: {
    bodyHtml: `<p>You can find your partnership agreement on your Hero Portal by following the steps below:</p>
<ul>
<li>Account</li>
<li>Documents</li>
<li>Scroll down to 'partnership agreement' and click on 'view'</li>
</ul>`,
  },
  [normKnowledgeKey('Will-offers-or-promos-affect-my-payment')]: {
    bodyHtml: "<p>Nope – any offers or promos we run won't impact what you earn.</p>",
  },
  [normKnowledgeKey('How-do-I-upload-a-new-document-to-my-profile')]: {
    bodyHtml: `<p>Open your Pro app and head to your Account</p>
<p>Select Documents – you can delete, add and replace documents here</p>`,
  },
  [normKnowledgeKey('How-does-payment-work-and-what-should-I-do-if-I-haven-t-been-paid')]: {
    bodyHtml: `<h3>We'll pay you every Friday</h3>
<p>We'll send you a weekly statement every Tuesday by 4pm, showing all your bookings from the week before. We'll then pay you for that week every Friday.</p>
<p>If there's ever a delay (like a bank holiday), we'll email you to let you know.</p>
<h3>Missing a payment? Check your bank details</h3>
<p>Head to the Statements section of your Urban Pro app and double-check your bank info. If everything looks right but you're still missing a payment, email <a href="mailto:info@urban.co">info@urban.co</a> with your statement ID.</p>
<p>We'll investigate and reprocess your payment if needed. If a payment bounces back, we'll contact you directly.</p>
<h3>Got a question about your statement? Let us know within 14 days</h3>
<p>If something doesn't look right, email <a href="mailto:info@urban.co">info@urban.co</a> within 14 days of the booking date. We won't be able to look into anything after this window.</p>`,
  },
  [normKnowledgeKey('How-to-use-the-Templates-feature')]: {
    bodyHtml: `<p>Service area templates let you set different service areas for different times and days in your calendar. For example, you might want to work in certain postcodes in the morning, or avoid certain areas in the evening.</p>
<h3>Finding templates in the app</h3>
<p>You'll find templates in the availability section of the Pro app under service area templates.</p>
<h3>Applying templates in your diary</h3>
<p>There's no limit to how many templates you can create, but we recommend starting small. Too many templates can get confusing.</p>
<p>Once you've created a template, you can apply it to any hours you add to your diary in the Urban Pro app.</p>
<p>Always tap save template. If you don't save, your changes won't apply and the usual cancellation policy will still stand.</p>
<h3>Use templates to:</h3>
<ul>
<li>Avoid expensive parking</li>
<li>Work closer to other commitments</li>
<li>Skip busy roads or stations</li>
<li>Feel safer when working late</li>
<li>Stay outside congestion charge zones</li>
</ul>
<h3>Avoiding congestion charge areas</h3>
<p>Congestion charge zones are listed separately in the app. If you want to avoid paying, don't add availability in:</p>
<ul>
<li>CGC North of River Thames</li>
<li>CGC South of River Thames</li>
<li>CGC City of London</li>
</ul>
<p>You can still work nearby – for example Belgravia/Pimlico – without entering the zone.</p>
<h3>Templates only apply to service areas</h3>
<p>Currently, you can't use templates to control when or where you offer certain treatments – but we're working on this for a future update.</p>`,
  },
  [normKnowledgeKey('Where-and-when-can-I-work-as-an-Urban-Pro')]: {
    bodyHtml: `<h3>Where you can work</h3>
<p>We're currently live in:</p>
<ul>
<li>London – Massage, osteopathy, physiotherapy, facials, nails, brows and lashes</li>
<li>Paris – Massage, osteopathy</li>
<li>Birmingham, Manchester, Liverpool – Massage, physiotherapy and facials</li>
<li>Edinburgh, Glasgow – Massage only</li>
</ul>
<p>As a self-employed Urban Pro, you choose the areas that work for you.</p>
<h3>How service areas work</h3>
<p>Each city is divided into smaller zones. You select the ones that suit where you're happy to travel – this helps make sure bookings are manageable while meeting client demand.</p>
<p>If you're just getting started, we recommend choosing at least 20 areas (especially in London Zones 1 &amp; 2) to increase your chances of getting booked.</p>
<h3>When you can work</h3>
<p>You're in control of your schedule. Add or remove availability anytime – it only takes a few taps.</p>
<p>You can:</p>
<ul>
<li>Work any day, all year round</li>
<li>Choose hours between 07:00 and 23:30</li>
<li>Set your availability in the app or on the website</li>
</ul>
<p>Work when it suits you – your calendar fits around your life</p>`,
  },
  [normKnowledgeKey('How-can-I-retain-my-customers')]: {
    bodyHtml: `<p>Beyond providing a great experience for customers in your bookings, remember that our customers are busy, time-poor professionals who value convenience. The most effective way to ensure that you're retaining your client base is to be available for them whenever they're searching.</p>
<p>You can do this by:</p>
<ol>
<li><strong>Adding your availability in advance</strong> – some of your clients will book monthly, while others may book every week. Make it as easy as possible for all of your users to rebook you, regardless of their booking pattern, by making sure you have availability in your diary for several weeks in advance.</li>
<li><strong>Manage your Service Areas</strong> – understand where your repeat users are booking, and be mindful of keeping yourself available in those areas, especially if you use several different Templates.</li>
<li><strong>Encourage your clients to rebook</strong> – include advice on when to next have a treatment in your aftercare advice and follow up on your session with a treatment report. You can use the HERO10 code to offer your client a 10% discount if they rebook with you on the spot.</li>
<li><strong>Stand out from the crowd</strong> – for more tips, tricks, and testimonials on how to build a loyal client base, check out our webinar <a href="https://youtu.be/LzjP-fRUqys?t=1072" target="_blank" rel="noopener noreferrer">here</a>.</li>
</ol>`,
  },
};

function heroArticleHref(locale: KnowledgeLocale, urlName: string): string {
  return `/${locale}/hero/article/${urlName}`;
}

function buildSafetyAtUrbanBody(locale: KnowledgeLocale): string {
  return `<p>We're committed to making Urban a safe and trusted place for everyone that uses it, both for booking treatments at home and for delivering wellness services. Find a guide to our safety features <a href="https://urban.co/en-gb/safety" target="_blank" rel="noopener noreferrer">here</a>.</p>
<p><strong>Here's an index of all Urban pro FAQ articles related to safety:</strong></p>
<ul>
<li><a href="${heroArticleHref(locale, 'How-to-respect-your-client-s-boundaries-during-a-booking')}">What should I do if I feel uncomfortable during a booking?</a></li>
<li><a href="${heroArticleHref(locale, 'Massage-towel-draping-a-quick-guide')}">Top tips for towel management</a></li>
<li><a href="${heroArticleHref(locale, 'How-does-Urban-keep-me-safe')}">What is Urban's relationship with law enforcement?</a></li>
<li><a href="${heroArticleHref(locale, 'What-if-a-client-makes-a-complaint')}">How do we handle sensitive complaints?</a></li>
<li><a href="${heroArticleHref(locale, 'Peoplesafe')}">What safety features does Urban offer?</a></li>
<li><a href="${heroArticleHref(locale, 'Protecting-your-data-and-confidentiality-on-Urban')}">Protecting your data and confidentiality on Urban</a></li>
<li><a href="${heroArticleHref(locale, 'Staying-safe-while-travelling-to-and-from-bookings')}">Staying safe while travelling to and from bookings</a></li>
<li><a href="${heroArticleHref(locale, 'What-is-Zecure')}">Understanding the safety settings built into your phone</a></li>
<li><a href="${heroArticleHref(locale, 'How-does-Urban-keep-me-safe')}">Who can I speak to about safety?</a></li>
</ul>`;
}

export function isHelpArticleRemoved(urlName: string): boolean {
  return HELP_REMOVED.has(normKnowledgeKey(urlName));
}

export function isHeroArticleRemoved(urlName: string): boolean {
  return HERO_REMOVED.has(normKnowledgeKey(urlName));
}

export function isHeroArticleMovedToHelp(urlName: string): boolean {
  return HERO_TO_HELP.has(normKnowledgeKey(urlName));
}

export function applyKnowledgeArticleAudit(
  article: KnowledgeArticle,
  channel: KnowledgeChannel,
  locale: KnowledgeLocale
): KnowledgeArticle {
  const key = normKnowledgeKey(article.urlName);
  let next = { ...article };

  const patch = ARTICLE_PATCHES[key];
  if (patch?.title) {
    next.title = patch.title;
  }
  if (patch?.bodyHtml) {
    next.bodyHtml = patch.bodyHtml;
  }

  if (key === normKnowledgeKey('Safety-at-Urban') && channel === 'hero') {
    next.bodyHtml = buildSafetyAtUrbanBody(locale);
  }

  if (key === normKnowledgeKey('Offering-physiotherapy-on-Urban') && channel === 'hero') {
    next.bodyHtml = next.bodyHtml.replace(
      'Physiotherapy is currently available in London only.',
      'Physiotherapy is currently available in London, Manchester and Birmingham only.'
    );
  }

  if (key === normKnowledgeKey('What-equipment-should-I-bring') && channel === 'hero') {
    next.bodyHtml = next.bodyHtml.replace(
      '<li>\n<p>Smartphone</p>\n</li>',
      '<li>\n<p>Smartphone</p>\n</li><li>\n<p>Charge and power bank</p>\n</li>'
    );
  }

  if (key === normKnowledgeKey('What-s-the-difference-between-Urban-classic-energising-and-relaxing-massages')) {
    next.bodyHtml = next.bodyHtml.replace(
      /<h3><span style="font-size:16px"><strong>Relaxing – the deep unwind<\/strong><\/span><\/h3>\s*<h3><span style="font-size:14px"><strong>Best for:<\/strong>[\s\S]*?<\/span><\/h3>/,
      `<h3>Relaxing – the deep unwind</h3>
<p><strong>Best for:</strong> Clients looking to de-stress and drift away<br />
<strong>Benefits:</strong> Stimulates the parasympathetic nervous system, promotes deep mental relaxation and improves sleep<br />
<strong>Pressure:</strong> Light<br />
<strong>Techniques:</strong> Flowing effleurage, petrissage, vibration and a scalp massage at the end for an extra soothing touch<br />
<strong>Client expectation:</strong> They want to feel calm, peaceful and stress-free. Adapt the treatment based on their stress levels and provide a full-body massage unless requested otherwise.</p>`
    );
  }

  return next;
}

function filterCategorySlugs(
  slugs: string[],
  channel: KnowledgeChannel,
  removedKeys: Set<string>
): string[] {
  const seen = new Set<string>();
  return slugs.filter((slug) => {
    const key = normKnowledgeKey(slug);
    if (removedKeys.has(key)) {
      return false;
    }
    if (channel === 'hero' && HERO_TO_HELP.has(key)) {
      return false;
    }
    if (channel === 'help' && HERO_REMOVED.has(key)) {
      return false;
    }
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function applyKnowledgeCategoriesAudit(
  categories: KnowledgeCategory[],
  channel: KnowledgeChannel,
  locale: KnowledgeLocale
): KnowledgeCategory[] {
  if (locale !== 'en-gb') {
    return categories;
  }

  const removedKeys =
    channel === 'help'
      ? new Set([...HELP_REMOVED, ...HERO_REMOVED])
      : new Set([...HERO_REMOVED, ...HERO_TO_HELP]);

  let next = categories.map((category) => ({
    ...category,
    articleSlugs: filterCategorySlugs(category.articleSlugs, channel, removedKeys),
  }));

  if (channel === 'help') {
    const physio = next.find((category) => category.slug === 'urban-physio');
    if (physio) {
      next = next.map((category) =>
        category.slug === 'service-etiquette'
          ? {
              ...category,
              articleSlugs: filterCategorySlugs(
                [...category.articleSlugs, ...physio.articleSlugs],
                channel,
                removedKeys
              ),
            }
          : category
      );
      next = next.filter((category) => category.slug !== 'urban-physio');
    }

    next = next.map((category) => {
      if (category.slug === 'amending-a-booking') {
        return {
          ...category,
          articleSlugs: category.articleSlugs.filter(
            (slug) =>
              normKnowledgeKey(slug) !== normKnowledgeKey('how-should-i-prepare-for-my-massage') &&
              normKnowledgeKey(slug) !== normKnowledgeKey('when-will-my-pro-arrive')
          ),
        };
      }

      if (category.slug === 'using-promotional-and-gift-codes') {
        return {
          ...category,
          name: 'Using promotional codes and gift cards',
          articleSlugs: category.articleSlugs.filter(
            (slug) => !normKnowledgeKey(slug).startsWith('urbanrewards')
          ),
        };
      }

      return category;
    });
  }

  return next.filter((category) => category.articleSlugs.length > 0);
}
